import type { AccountManager, User } from './_types';
import type { Client } from 'src/models/client/_types';
import type { Permissions } from 'src/shared/usePermissionCheck';

export type { User, AccountManager } from './_types';

export const getUserDisplayName = (user?: User | Client, label: string = 'Unknown'): string => {
  if (!user) return label;
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  if ('email' in user && user.email) {
    return user.email;
  }
  if ('clientName' in user && user.clientName) {
    return user.clientName;
  }
  return label;
};

export const isHeadTrainer = (user?: User): boolean => {
  return !!user && user.trainerId === user.id;
};

export const isSubTrainer = (user?: User): boolean => {
  return !!user && !!user.headTrainerId;
};

export const isProTrainer = (user?: User | null): boolean => {
  return !!user && (user.payingTrainer || !!user.headTrainerId);
};

export const isManager = (user?: User | null): boolean => {
  return !!user?.roles?.includes('manager');
};

export const isAdmin = (user?: User | null): boolean => {
  return !!user?.roles?.includes('admin');
};

export const isSuperAdmin = (user?: User | null): boolean => {
  return !!user?.roles?.includes('super_admin');
};

export const isSupportRole = (user?: User | null): boolean => {
  return !!user?.roles?.includes('support');
};

export const isTrainer = (user?: User): boolean =>
  !!user && (isHeadTrainer(user) || isSubTrainer(user));

export const getUserInitials = (user: User): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
  }
  if (user.nickname) {
    return user.nickname.charAt(0);
  }
  if (user.email) {
    return user.email.charAt(0);
  }
  return '00';
};

export const getDependentInitials = (accountManager: AccountManager): string => {
  const parts = (accountManager.dependentName || '').split(' ').filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
  }
  if (accountManager.dependentEmail) {
    return accountManager.dependentEmail.charAt(0);
  }
  return '00';
};

export const getDependentDisplayName = (accountManager?: AccountManager): string => {
  if (!accountManager) return 'Unknown';
  if (accountManager.dependentName) return accountManager.dependentName;
  if (accountManager.dependentEmail) return accountManager.dependentEmail;
  return 'Unknown';
};

export const getUserAddress = (user: User): string => {
  const addressParts = [user.streetAddress, user.city, user.state, user.zipCode];
  const nonEmptyParts = addressParts.filter((part) => !!part && String(part).trim() !== '');
  return nonEmptyParts.join(', ');
};

export const getClientAutocompleteKey = (user: User, permissions: Permissions): string => {
  if (permissions.canOnlyManageLocationClient && !!user.locationId) {
    return `client_location_${user.locationId}`;
  }
  if (permissions.canManageClientsForTrainer) {
    return `client_trainer_${user.trainerId}`;
  }
  return `client_trainer_${user.id}`;
};

export const defaultStats = [
  { stat: 'weight', label: 'Weight', value: 'weight' },
  { stat: 'height', label: 'Height', value: 'height' },
  { stat: 'bmi', label: 'BMI', value: 'bmi' },
  { stat: 'systolic_bp', label: 'Systolic BP', value: 'systolic_bp' },
  { stat: 'diastolic_bp', label: 'Diastolic BP', value: 'diastolic_bp' },
  { stat: 'body_fat', label: 'Body Fat %', value: 'body_fat' },
  { stat: 'rhr', label: 'Resting Heart Rate', value: 'rhr' },
  { stat: 'waist', label: 'Waist Size', value: 'waist' },
  { stat: 'thigh', label: 'Thigh Size', value: 'thigh' },
  { stat: 'bicep', label: 'Bicep Size', value: 'bicep' },
  { stat: 'blood_pressure', label: 'Blood Pressure', value: null, metric: null },
];

export const statsForPlatform = (trainer: User) => {
  const stats: any[] = [];
  stats.push(...defaultStats);

  if ((trainer as any).healthInfoMetrics) {
    stats.push(
      ...((trainer as any).healthInfoMetrics as string[]).map((stat) => {
        return { stat: stat, label: stat, value: stat };
      })
    );
  }

  // defaultHealthMetrics are the stats we *DON'T* want to include
  return stats.filter((r) => !(trainer as any).defaultHealthMetrics?.includes(r.label));
};


